import { useMutation, useQuery } from '@tanstack/react-query';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { useEffect, useRef, useState } from 'react';
import {
  endpointCompaniesHouseSearchGET,
  endpointCompaniesHouseShareholdersGET,
  GetCompaniesHouseSearchResponse,
  GetCompaniesHouseShareholdersResponse,
} from '~/api/def/endpoints/companiesHouseEndpoint';
import { agTheme } from '~/components/ui/tables/ag/agTheme';

export type ShareholderRow = GetCompaniesHouseShareholdersResponse['data'][0];

type CompaniesHouseImportProps = {
  companyName?: string;
  onImportSuccess: (
    companyName: string,
    companyNumber: string,
    shareholders: ShareholderRow[],
  ) => void;
};

export const CompaniesHouseImport = ({
  companyName,
  onImportSuccess,
}: CompaniesHouseImportProps) => {
  const toast = useRef<Toast>(null);

  const [selectedCompany, setSelectedCompany] =
    useState<GetCompaniesHouseSearchResponse['data']['items'][0]>();

  const {
    data: companiesData,
    refetch: searchCompanies,
    isFetching: isSearching,
    isError: isSearchError,
  } = useQuery({
    queryKey: ['companiesHouseSearch', companyName],
    queryFn: () => endpointCompaniesHouseSearchGET(companyName ?? ''),
    enabled: false,
    retry: 2,
  });

  useEffect(() => {
    if (isSearchError) {
      setSelectedCompany(undefined);

      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Could not find company',
      });
    }
  }, [isSearchError]);

  const {
    mutate: importShareholders,
    data: shareholdersData,
    isPending: isImporting,
    isError: isImportError,
  } = useMutation<ShareholderRow[], Error, string>({
    mutationFn: (companyNumber: string) =>
      endpointCompaniesHouseShareholdersGET(companyNumber),
    onSuccess: (data) => {
      if (!selectedCompany) return;

      onImportSuccess(
        selectedCompany.companyName,
        selectedCompany.companyNumber,
        data,
      );
    },
    retry: 2,
  });

  useEffect(() => {
    if (isImportError) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Could not import shareholders',
      });
    }
  }, [isImportError]);

  const handleSearch = async () => {
    if (companyName) {
      const result = await searchCompanies();
      setSelectedCompany(result.data?.topHit);
    }
  };

  const handleImport = () => {
    if (selectedCompany) {
      importShareholders(selectedCompany.companyNumber);
    }
  };

  const gridRowData =
    shareholdersData && Array.isArray(shareholdersData)
      ? shareholdersData
      : shareholdersData
        ? [shareholdersData]
        : [];

  const columnDefs: ColDef<ShareholderRow>[] = [
    { headerName: 'Name', field: 'name' },
    { headerName: 'Share Class', field: 'shareClass' },
    { headerName: 'Amount', field: 'shareAmount' },
    { headerName: 'Nominal Value', field: 'nominalValue' },
    { headerName: 'Currency', field: 'currency' },
  ];

  const selectOptions = companiesData?.items.map((company) => ({
    label: company.companyName,
    value: company,
  }));

  return (
    <div className="flex flex-column w-full gap-2">
      <h3>Companies House</h3>
      <div className="flex flex-column gap-2">
        <div className="flex flex-row gap-2">
          <div className="w-16rem">{companyName?.toUpperCase() ?? '-'}</div>
          <Button type="button" onClick={handleSearch} disabled={isSearching}>
            Search
          </Button>
        </div>

        <div className="flex flex-row gap-2">
          <Dropdown
            options={selectOptions ?? []}
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.value)}
            placeholder="Select a company"
            className="w-16rem"
          />
          <Button
            type="button"
            onClick={handleImport}
            disabled={!selectedCompany || isImporting}
          >
            {isImporting ? 'Importing...' : 'Import'}
          </Button>
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        <AgGridReact<ShareholderRow>
          columnDefs={columnDefs}
          rowData={gridRowData}
          pagination={true}
          theme={agTheme}
          suppressCellFocus
          tooltipShowDelay={0}
          domLayout="autoHeight"
          autoSizeStrategy={{
            type: 'fitGridWidth',
            defaultMinWidth: 100,
          }}
        />

        {isImporting && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255, 255, 255, 0.7)',
              zIndex: 10,
            }}
          >
            <ProgressSpinner
              style={{ width: '50px', height: '50px' }}
              strokeWidth="5"
            />
          </div>
        )}
      </div>
      {isImporting && (
        <p>We are importing the shareholders. This may take a few seconds.</p>
      )}
      <Toast ref={toast} position="bottom-center" />
    </div>
  );
};
